import { FunctionComponent, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Grid, TextAreaField, TextField } from '@backstage/ui';
import { isPluginEnabled, RequiredStar, StepTimeline, SummaryValue } from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeServiceGroup } from '../../../api/service-groups/types';
import {
  applicationKeyPrefix,
  isValidApplicationDescription,
  isValidApplicationKey,
  isValidApplicationName,
} from '../../../utils/service-groups/keys';
import shared from '../../../styles.module.css';
import styles from './CreateSonarQubeServiceGroupPage.module.css';

/** Step labels of the create flow. */
export const sonarQubeServiceGroupSteps = ['General information', 'Summary'];

/** The parts of a product team this flow needs. */
export interface SonarQubeProductTeam {
  id: string;
  nameCleaned: string;
}

// Form state lives in a hook the page calls, because the page's step timeline owns Next/Create.
export const useCreateSonarQubeServiceGroup = (
  team: SonarQubeProductTeam | undefined,
) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  // null until edited, so the key is prefilled with `{team}_` once the team loads.
  const [keyInput, setApplicationKey] = useState<string | null>(null);
  const [applicationName, setApplicationName] = useState('');
  const [applicationDescription, setApplicationDescription] = useState('');

  const values = {
    applicationKey: (
      keyInput ?? (team ? applicationKeyPrefix(team.nameCleaned) : '')
    )
      .trim()
      .toLowerCase(),
    applicationName: applicationName.trim(),
    applicationDescription: applicationDescription.trim(),
  };
  const valid = {
    applicationKey: isValidApplicationKey(values.applicationKey),
    applicationName: isValidApplicationName(values.applicationName),
    applicationDescription: isValidApplicationDescription(
      values.applicationDescription,
    ),
  };

  const [submitState, runSubmit] = useAsyncFn(async () => {
    if (!team) throw new Error('Product team is still loading.');
    const { id } = await sonarqubeApi.createServiceGroup({
      productTeamId: team.id,
      applicationKey: values.applicationKey,
      applicationName: values.applicationName,
      applicationDescription: values.applicationDescription,
    });
    return id;
  }, [
    sonarqubeApi,
    team,
    values.applicationKey,
    values.applicationName,
    values.applicationDescription,
  ]);

  /** `step` is 1-based within `sonarQubeServiceGroupSteps`. */
  const isStepValid = (step: number) =>
    step === 1
      ? valid.applicationKey &&
        valid.applicationName &&
        valid.applicationDescription
      : Boolean(team) && !submitState.loading;

  return {
    values,
    valid,
    inputs: { applicationName, applicationDescription },
    setApplicationKey,
    setApplicationName,
    setApplicationDescription,
    isStepValid,
    // useAsyncFn resolves with the Error instead of rejecting; truthy only when the group was created.
    submit: async () => { const r = await runSubmit(); return typeof r === "string" && r },
    submitState,
  };
};

export type CreateSonarQubeServiceGroupForm = ReturnType<
  typeof useCreateSonarQubeServiceGroup
>;

/** The parts of the form the steps render, shared by the create and update flows. */
export type SonarQubeServiceGroupFormFields = Pick<
  CreateSonarQubeServiceGroupForm,
  | 'values'
  | 'valid'
  | 'inputs'
  | 'setApplicationKey'
  | 'setApplicationName'
  | 'setApplicationDescription'
> & { submitState: { error?: Error } };

interface CreateSonarQubeServiceGroupProps {
  step: number;
  form: SonarQubeServiceGroupFormFields;
  /** Set when updating: locks the key and shows the changes in the summary. */
  previous?: SonarQubeServiceGroup;
}

/** Renders `step` (1-based within `sonarQubeServiceGroupSteps`) of the service group flow. */
export const CreateSonarQubeServiceGroup: FunctionComponent<
  CreateSonarQubeServiceGroupProps
> = ({ step, form, previous }) => {
  const { values, valid, inputs, submitState } = form;

  if (step === 1) {
    return (
      <div className={shared.stepSection}>
        <div className={shared.formRow}>
          <div className={shared.formLabelCol}>
            <span className={shared.formLabel}>
              Application key
              <RequiredStar />
            </span>
            <span className={shared.hintText}>
              Prefixes every project key and group name. Cannot be changed
              later.
            </span>
          </div>
          <div className={shared.formContent}>
            <div className={shared.formQuestionGroup}>
              <span className={shared.formQuestion}>
                Key in the format team_product
              </span>
              <span className={shared.hintText}>
                Letters, digits, '-', '.' and ':' with a single '_', at most 100
                characters
              </span>
            </div>
            <TextField
              aria-label="Application key"
              className={shared.textFieldControl}
              value={values.applicationKey}
              onChange={form.setApplicationKey}
              isInvalid={!valid.applicationKey}
              isDisabled={Boolean(previous)}
            />
          </div>
        </div>

        <div className={shared.formRow}>
          <span className={shared.formLabel}>
            Application name
            <RequiredStar />
          </span>
          <div className={shared.formContent}>
            <div className={shared.formQuestionGroup}>
              <span className={shared.formQuestion}>
                Display name in SonarQube
              </span>
              <span className={shared.hintText}>
                Letters, digits, spaces and '.', '_', ':', '(', ')', '-'
              </span>
            </div>
            <TextField
              aria-label="Application name"
              className={shared.textFieldControl}
              value={inputs.applicationName}
              onChange={form.setApplicationName}
              isInvalid={!valid.applicationName}
            />
          </div>
        </div>

        <div className={shared.formRow}>
          <span className={shared.formLabel}>Description</span>
          <div className={shared.formContent}>
            <span className={shared.formQuestion}>Optional, a single line</span>
            <TextAreaField
              aria-label="Application description"
              className={shared.textFieldControl}
              value={inputs.applicationDescription}
              onChange={form.setApplicationDescription}
              isInvalid={!valid.applicationDescription}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shared.stepSection}>
      <div className={shared.summaryBox}>
        <div>
          <h2 className={shared.summaryHeading}>General information</h2>
          <Grid.Root columns="3" gap="14">
            <SummaryValue label="Group type" value="SonarQube" />
            <SummaryValue
              label="Application key"
              value={values.applicationKey}
            />
            <SummaryValue
              label="Application name"
              value={values.applicationName}
              previousValue={previous?.applicationName}
            />
            <SummaryValue
              label="Description"
              value={values.applicationDescription}
              previousValue={previous?.applicationDescription}
            />
          </Grid.Root>
        </div>
        <span className={shared.hintText}>
          {previous
            ? 'Updates the name and description of the SonarQube application and the tooling group.'
            : `Creates the SonarQube application, the ${values.applicationKey}_admin and ${values.applicationKey}_developer groups, and a permission template. You join the admin group.`}
        </span>
        {submitState.error && (
          <span role="alert" className={styles.error}>
            {submitState.error.message}
          </span>
        )}
      </div>
    </div>
  );
};

interface CreateSonarQubeServiceGroupPageProps {
  /** The team the group is created for; the app shell fetches it, since this library can't. */
  team?: SonarQubeProductTeam;
}

/** Route: /product-teams/:id/tooling-groups/sonarqube/create */
export const CreateSonarQubeServiceGroupPage = ({
  team,
}: CreateSonarQubeServiceGroupPageProps) => {
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [activeStep, setActiveStep] = useState(1);
  const form = useCreateSonarQubeServiceGroup(team);
  const teamPath = `/product-teams/${encodeURIComponent(id)}`;

  if (!isPluginEnabled(configApi, 'sonarqube')) {
    return <Navigate to="/" replace />;
  }

  const goToNextStep = async () => {
    if (activeStep < sonarQubeServiceGroupSteps.length) {
      setActiveStep(step => step + 1);
      return;
    }
    const serviceGroupId = await form.submit();
    if (serviceGroupId) {
      navigate(
        `${teamPath}/tooling-groups/sonarqube/${encodeURIComponent(
          serviceGroupId,
        )}`,
      );
    }
  };

  return (
    <div className={styles.body}>
      <StepTimeline
        steps={sonarQubeServiceGroupSteps}
        activeStep={activeStep}
        onCancel={() => navigate(teamPath)}
        onPrevious={() => setActiveStep(step => Math.max(step - 1, 1))}
        onNext={goToNextStep}
        isNextDisabled={!form.isStepValid(activeStep)}
        isSubmitting={form.submitState.loading}
      >
        <CreateSonarQubeServiceGroup step={activeStep} form={form} />
      </StepTimeline>
    </div>
  );
};
